// src/NdviLayer.jsx
import React, { useEffect, useState, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

// --- Constants & Helpers ---

// Standard GDAL NDVI range mapping
const DEFAULT_MIN_NDVI = -1.0;
const DEFAULT_MAX_NDVI = 0.92590492963791;

const toDateKey = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
  return null;
};

// Color Ramp Function: Maps NDVI (-1 to 1) to RGBA
function colorRamp(ndvi) {
  const v = Math.max(-1, Math.min(1, ndvi));
  const t = (v + 1) / 2; // Normalize to 0-1
  if (t < 0.5) {
    // Red to Yellow
    const f = t / 0.5;
    const r = 255;
    const g = Math.round(255 * f);
    return [r, g, 0, 220];
  } else {
    // Yellow to Green
    const f = (t - 0.5) / 0.5;
    const r = Math.round(255 * (1 - f));
    const g = 255;
    return [r, g, 0, 220];
  }
}

// Coordinate Conversion (Web Mercator -> LatLng)
const mercatorToLatLng = (x, y) => {
  const rMajor = 6378137;
  const shift = Math.PI * rMajor;
  const lon = (x / shift) * 180.0;
  let lat = (y / shift) * 180.0;
  lat = (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180.0)) - Math.PI / 2.0);
  return [lat, lon];
};

// --- Sub-Component: Handles the Canvas/Pixel Logic for ONE Layer ---
function NdviSingleLayer({ url, bounds, opacity, filterMin, filterMax, zIndex, pane }) {
  const map = useMap();
  const layerRef = useRef(null);
  const optsRef = useRef({ filterMin, filterMax });

  // Keep refs updated for the GridLayer to access inside createTile
  useEffect(() => {
    optsRef.current = { filterMin, filterMax };
    if (layerRef.current) layerRef.current.redraw();
  }, [filterMin, filterMax]);

  // 1. Initialize the Custom Grid Layer
  useEffect(() => {
    const Grid = L.GridLayer.extend({
      createTile: function (coords, done) {
        const tile = L.DomUtil.create("canvas", "leaflet-tile");
        const size = this.getTileSize();
        tile.width = size.x;
        tile.height = size.y;
        const ctx = tile.getContext("2d");

        const img = new Image();
        // Vital for canvas manipulation on external images
        img.crossOrigin = "Anonymous"; 
        
        const tileUrl = url
          .replace("{z}", coords.z)
          .replace("{x}", coords.x)
          .replace("{y}", coords.y);

        img.onload = () => {
          // Clean up memory if tile was removed before load
          if (!tile.parentNode && !map.hasLayer(layer)) return;

          const off = document.createElement("canvas");
          off.width = tile.width;
          off.height = tile.height;
          const octx = off.getContext("2d");
          octx.drawImage(img, 0, 0, tile.width, tile.height);

          try {
            // Get pixel data
            const src = octx.getImageData(0, 0, tile.width, tile.height).data;
            const out = ctx.createImageData(tile.width, tile.height);

            for (let i = 0; i < src.length; i += 4) {
              const enc = src[i]; // Red channel holds the encoded value
              const alpha = src[i+3];

              // Transparent or No Data (255 usually nodata in GDAL grayscale)
              if (alpha === 0 || enc === 255) {
                out.data[i + 3] = 0;
                continue;
              }

              // Calculate NDVI
              const ndvi = DEFAULT_MIN_NDVI + (enc / 254.0) * (DEFAULT_MAX_NDVI - DEFAULT_MIN_NDVI);

              // Filter based on Sidebar Slider
              if (ndvi < optsRef.current.filterMin || ndvi > optsRef.current.filterMax) {
                out.data[i + 3] = 0; // Hide pixel
                continue;
              }

              // Apply Color Ramp
              const [r, g, b, a] = colorRamp(ndvi);
              out.data[i] = r;
              out.data[i + 1] = g;
              out.data[i + 2] = b;
              out.data[i + 3] = a; 
            }
            ctx.putImageData(out, 0, 0);
            done(null, tile);
          } catch (err) {
            console.warn("NDVI processing error (CORS?):", err);
            done(err, tile);
          }
        };
        
        img.onerror = () => done(null, tile);
        img.src = tileUrl;
        return tile;
      },
    });

    const layer = new Grid({ 
        tileSize: 256, 
        opacity: opacity, 
        // FIX: Use the zIndex passed from props so layers stack correctly
        zIndex: zIndex, 
        tms: true, // Vital for TMS (Y-flipped) tiles
        pane: pane || 'overlayPane'
    });

    layer.addTo(map);
    layerRef.current = layer;

    return () => {
      if (map.hasLayer(layer)) map.removeLayer(layer);
    };
  }, [map, url, zIndex, pane]); 

  // 2. Handle Opacity Updates
  useEffect(() => {
    if (layerRef.current) layerRef.current.setOpacity(opacity);
  }, [opacity]);

  // 3. Handle Hover Sampling (Mouse Move)
  // Note: Only the top layer typically needs to dispatch hover events, 
  // but if multiple are active, this might trigger multiple events.
  // For now, kept as is.
  useEffect(() => {
    let mounted = true;

    async function sampleTile(latlng) {
      if (!latlng) return;
      const z = map.getZoom();
      const p = map.project(latlng, z);
      const tileSize = 256;
      const tileX = Math.floor(p.x / tileSize);
      const tileY = Math.floor(p.y / tileSize);
      const px = Math.floor(p.x % tileSize);
      const py = Math.floor(p.y % tileSize);

      const tileUrl = url
        .replace("{z}", z)
        .replace("{x}", tileX)
        .replace("{y}", tileY);

      try {
        const resp = await fetch(tileUrl, { mode: "cors" });
        if (!resp.ok) throw new Error("Tile fetch failed");
        
        const blob = await resp.blob();
        const imgBitmap = await createImageBitmap(blob);
        
        const off = document.createElement("canvas");
        off.width = tileSize;
        off.height = tileSize;
        const cx = off.getContext("2d");
        cx.drawImage(imgBitmap, 0, 0);
        
        const data = cx.getImageData(px, py, 1, 1).data;
        const enc = data[0];
        const alpha = data[3];

        let val = null;
        if (alpha !== 0 && enc !== 255) {
          val = DEFAULT_MIN_NDVI + (enc / 254.0) * (DEFAULT_MAX_NDVI - DEFAULT_MIN_NDVI);
        }
        
        window.dispatchEvent(new CustomEvent("ndvi-hover-value", { detail: { value: val } }));

      } catch (err) {
        window.dispatchEvent(new CustomEvent("ndvi-hover-value", { detail: { value: null } }));
      }
    }

    function moveHandler(e) {
      if (!mounted) return;
      sampleTile(e.latlng);
    }

    if (layerRef.current) {
        map.on("mousemove", moveHandler);
    }
    
    return () => {
      mounted = false;
      map.off("mousemove", moveHandler);
    };
  }, [map, url]);

  return null;
}


// --- Main Component: Manager ---
export default function NdviLayer({ latestSurvey, pane, apiBase }) {
  const map = useMap();
  
  const [layers, setLayers] = useState([]);
  const [globalFilterMin, setGlobalFilterMin] = useState(DEFAULT_MIN_NDVI);
  const [globalFilterMax, setGlobalFilterMax] = useState(DEFAULT_MAX_NDVI);
  
  const isCancelledRef = useRef(false);
  const hasZoomedRef = useRef(false);

  // --- URL Builder ---
  const buildNdviUrl = (cand, latestSurvey, index) => {
    if (!latestSurvey?.location_name || !latestSurvey?.survey_time) return null;
    
    const loc = encodeURIComponent(String(latestSurvey.location_name));
    const sv = toDateKey(latestSurvey.survey_time);
    
    // FIX: Ensure unique identifier. Fallback to index if slug is missing to prevent duplicates.
    const rawLayerName = cand.layer_slug || cand.layer_name || `layer-${index}`;
    const layer = encodeURIComponent(rawLayerName);
    
    // Matches Backend Route: /ndvi/:loc/:survey/:layer/tiles/:z/:x/:y
    return `${apiBase}/ndvi/${loc}/${sv}/${layer}/tiles/{z}/{x}/{y}.png`;
  };

  // --- Fetch & Parse XML for Bounds ---
  const fetchLayerMetadata = async (tileUrlTemplate) => {
    if (!tileUrlTemplate) return null;
    try {
      const xmlUrl = tileUrlTemplate.replace(/\{z\}\/\{x\}\/\{y\}\.png$/, "tilemapresource.xml");
      const resp = await fetch(xmlUrl);
      if (!resp.ok) return null;
      
      const text = await resp.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, "text/xml");
      const bbox = xmlDoc.querySelector("BoundingBox");

      if (bbox) {
        const minx = parseFloat(bbox.getAttribute("minx"));
        const miny = parseFloat(bbox.getAttribute("miny"));
        const maxx = parseFloat(bbox.getAttribute("maxx"));
        const maxy = parseFloat(bbox.getAttribute("maxy"));

        let sw, ne;
        if (Math.abs(minx) <= 180 && Math.abs(miny) <= 90) {
            sw = [miny, minx]; // [Lat, Lon]
            ne = [maxy, maxx];
        } else {
            sw = mercatorToLatLng(minx, miny);
            ne = mercatorToLatLng(maxx, maxy);
        }
        return [sw, ne]; 
      }
    } catch (e) { console.warn("NDVI XML Error:", e); }
    return null;
  };

  // --- Discovery Logic ---
  const discoverNdviLayers = async () => {
    if (!latestSurvey?.location_name || !latestSurvey?.survey_time) {
      setLayers([]);
      return;
    }

    try {
      const params = new URLSearchParams({
        location_name: String(latestSurvey.location_name),
        survey_time: toDateKey(latestSurvey.survey_time),
      });
      
      const url = `${apiBase}/ndvi/tiles-discovery?${params.toString()}`;
      
      console.log(`[NdviLayer] Discovering: ${url}`);
      const r = await fetch(url);
      if (!r.ok) throw new Error(`Failed to fetch layers (Status: ${r.status})`);
      
      const json = await r.json();
      const foundCandidates = Array.isArray(json.found) ? json.found : [];
      
      if (isCancelledRef.current) return;

      const processedLayers = await Promise.all(foundCandidates.map(async (cand, index) => {
        const layerName = cand.layer_slug || cand.layer_name || `NDVI ${index + 1}`;
        // Ensure ID is unique even if names are identical
        const uniqueId = `auto-ndvi-${layerName}-${index}`; 
        
        // Pass index to builder to ensure URL uniqueness
        const tileUrl = buildNdviUrl(cand, latestSurvey, index);
        const bounds = await fetchLayerMetadata(tileUrl);

        return {
          id: uniqueId,
          name: layerName,
          url: tileUrl,
          bounds: bounds,
          opacity: 0.85,
          visible: true,
          // FIX: Assign unique Z-index so layers stack
          zIndex: 500 + index
        };
      }));

      setLayers(processedLayers);
      hasZoomedRef.current = false;

      window.dispatchEvent(new CustomEvent("ndvi-layers-discovered", {
         detail: { 
           location: latestSurvey.location_name, 
           survey_time: latestSurvey.survey_time, 
           layer_names: processedLayers.map(l => l.name)
         }
      }));

    } catch (err) {
      console.error("[NdviLayer] Discovery error:", err);
      setLayers([]);
    }
  };

  // --- Effects ---

  useEffect(() => {
    isCancelledRef.current = false;
    discoverNdviLayers();
    return () => { isCancelledRef.current = true; };
  }, [latestSurvey, apiBase]);

  useEffect(() => {
    if (layers.length > 0 && !hasZoomedRef.current) {
      const layerWithBounds = layers.find(l => l.bounds);
      if (layerWithBounds) {
        map.fitBounds(layerWithBounds.bounds, { padding: [20, 20] });
        hasZoomedRef.current = true;
      }
    }
  }, [layers, map]);

  useEffect(() => {
    const handleToggle = (ev) => {
      const { id, visible } = ev?.detail || {};
      setLayers(prev => prev.map(l => (l.id === id || l.name === id) ? { ...l, visible: !!visible } : l));
    };

    const handleOpacity = (ev) => {
      const { id, opacity } = ev?.detail || {};
      setLayers(prev => prev.map(l => (l.id === id || l.name === id) ? { ...l, opacity: Number(opacity) } : l));
    };

    const handleNdviParams = (ev) => {
        const { min, max } = ev?.detail || {};
        if (min !== undefined) setGlobalFilterMin(Number(min));
        if (max !== undefined) setGlobalFilterMax(Number(max));
    };

    const handleDataUpdate = (ev) => {
        const detail = ev.detail || {};
        if (latestSurvey && 
            detail.location_name === latestSurvey.location_name && 
            toDateKey(detail.survey_time) === toDateKey(latestSurvey.survey_time)) {
            discoverNdviLayers();
        }
    };

    window.addEventListener("map-toggle-layer", handleToggle);
    window.addEventListener("map-set-opacity", handleOpacity);
    window.addEventListener("ndvi-set-params", handleNdviParams);
    window.addEventListener("survey-data-updated", handleDataUpdate);

    return () => {
      window.removeEventListener("map-toggle-layer", handleToggle);
      window.removeEventListener("map-set-opacity", handleOpacity);
      window.removeEventListener("ndvi-set-params", handleNdviParams);
      window.removeEventListener("survey-data-updated", handleDataUpdate);
    };
  }, [latestSurvey]);

  // --- Render ---
  return (
    <>
      {layers.map(layer => (
        layer.visible && (
          <NdviSingleLayer
            key={layer.id}
            url={layer.url}
            bounds={layer.bounds}
            opacity={layer.opacity}
            filterMin={globalFilterMin}
            filterMax={globalFilterMax}
            pane={pane}
            // FIX: Pass the calculated zIndex
            zIndex={layer.zIndex}
          />
        )
      ))}
    </>
  );
}